import React, { useState } from "react";
import {
  VLKPageHeader, VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent,
  VLKButton, VLKInput, VLKLabel, VLKSelect, VLKTextarea, VLKStatusBadge,
  VLKRiskMeter, VLKKPICard, VLKAlertDialog, VLKUserAvatar, VLKEmptyState,
  VLKDataTable, VLKTabs, toast,
} from "@/components/vlk";
import { ShieldCheck, Lock, Clock, Sparkles, Bell } from "lucide-react";

const swatch = [
  { name: "background",       token: "--background" },
  { name: "foreground",       token: "--foreground" },
  { name: "primary",          token: "--primary" },
  { name: "secondary",        token: "--secondary" },
  { name: "muted",            token: "--muted" },
  { name: "border",           token: "--border" },
  { name: "success",          token: "--success" },
  { name: "warning",          token: "--warning" },
  { name: "info",             token: "--info" },
  { name: "destructive",      token: "--destructive" },
  { name: "risk-low",         token: "--risk-low" },
  { name: "risk-medium",      token: "--risk-medium" },
  { name: "risk-high",        token: "--risk-high" },
  { name: "risk-critical",    token: "--risk-critical" },
];

export default function DesignSystem() {
  const [tab, setTab] = useState("tokens");
  const [openAlert, setOpenAlert] = useState(false);

  return (
    <div>
      <VLKPageHeader
        title="Design system"
        description="VeraLok's single source of truth for tokens, components and patterns. All UI derives from here."
      />

      <VLKTabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "tokens", label: "Tokens" },
          { value: "typography", label: "Typography" },
          { value: "components", label: "Components" },
          { value: "patterns", label: "Patterns" },
        ]}
      />

      <div className="mt-6 space-y-6">
        {tab === "tokens" && (
          <>
            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Colors</VLKCardTitle></VLKCardHeader>
              <VLKCardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {swatch.map((s) => (
                    <div key={s.token} className="border border-border rounded-sm p-3">
                      <div className="h-12 w-full rounded-sm border border-border" style={{ background: `hsl(var(${s.token}))` }} />
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{s.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground/60">{s.token}</div>
                    </div>
                  ))}
                </div>
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Spacing & radii</VLKCardTitle></VLKCardHeader>
              <VLKCardContent>
                <div className="grid grid-cols-4 gap-4">
                  {[2,4,6,8].map((r) => (
                    <div key={r} className="text-center">
                      <div className={`w-full h-12 bg-primary/10 border border-border rounded-[${r}px]`} />
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r}px</div>
                    </div>
                  ))}
                </div>
              </VLKCardContent>
            </VLKCard>
          </>
        )}

        {tab === "typography" && (
          <VLKCard>
            <VLKCardHeader><VLKCardTitle>Type scale</VLKCardTitle></VLKCardHeader>
            <VLKCardContent className="space-y-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Display · Cabinet Grotesk</div>
                <div className="font-display text-5xl tracking-tight">Identity you can lock.</div>
                <div className="font-display text-3xl tracking-tight mt-2">Trust you can verify.</div>
                <div className="font-display text-xl tracking-tight mt-2">Audit that is tamper-evident.</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Body · Manrope</div>
                <p className="text-base">
                  VeraLok is the zero-trust institutional identity platform used by
                  governments, regulators, banks, healthcare and universities.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Secondary text uses reduced contrast for hierarchy.
                </p>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Monospace · IBM Plex Mono</div>
                <div className="font-mono text-sm">123456782 · 2026-07-15T17:00:00Z · integrity_hash=<span className="text-muted-foreground">7f0a…c2e</span></div>
              </div>
            </VLKCardContent>
          </VLKCard>
        )}

        {tab === "components" && (
          <>
            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Buttons · VLKButton</VLKCardTitle></VLKCardHeader>
              <VLKCardContent className="flex flex-wrap gap-3">
                <VLKButton>Primary</VLKButton>
                <VLKButton variant="secondary">Secondary</VLKButton>
                <VLKButton variant="outline">Outline</VLKButton>
                <VLKButton variant="ghost">Ghost</VLKButton>
                <VLKButton variant="destructive">Destructive</VLKButton>
                <VLKButton loading>Loading</VLKButton>
                <VLKButton disabled>Disabled</VLKButton>
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Inputs · VLKInput · VLKSelect · VLKTextarea</VLKCardTitle></VLKCardHeader>
              <VLKCardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5"><VLKLabel>Email</VLKLabel><VLKInput placeholder="operator@veralok.io" /></div>
                <div className="space-y-1.5"><VLKLabel>Role</VLKLabel>
                  <VLKSelect><option>system_admin</option><option>compliance_officer</option></VLKSelect>
                </div>
                <div className="space-y-1.5"><VLKLabel>Notes</VLKLabel><VLKTextarea placeholder="Compliance note…" /></div>
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Badges & risk</VLKCardTitle></VLKCardHeader>
              <VLKCardContent className="flex flex-wrap gap-3 items-center">
                <VLKStatusBadge status="active">Active</VLKStatusBadge>
                <VLKStatusBadge status="locked">Locked</VLKStatusBadge>
                <VLKStatusBadge status="pending">Pending</VLKStatusBadge>
                <VLKStatusBadge status="verified">Verified</VLKStatusBadge>
                <VLKStatusBadge status="rejected">Rejected</VLKStatusBadge>
                <div className="ml-6 space-y-2">
                  <VLKRiskMeter level="low" />
                  <VLKRiskMeter level="medium" />
                  <VLKRiskMeter level="high" />
                  <VLKRiskMeter level="critical" />
                </div>
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>KPI cards · VLKKPICard</VLKCardTitle></VLKCardHeader>
              <VLKCardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <VLKKPICard label="Identities" value="24,819" icon={ShieldCheck} delta={12} />
                  <VLKKPICard label="Locked" value="47" icon={Lock} tone="danger" delta={-3} />
                  <VLKKPICard label="Pending" value="9" icon={Clock} tone="warning" />
                  <VLKKPICard label="Audit / 24h" value="1,204" icon={Sparkles} tone="info" />
                </div>
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Dialogs · VLKAlertDialog</VLKCardTitle></VLKCardHeader>
              <VLKCardContent>
                <VLKButton variant="destructive" onClick={() => setOpenAlert(true)}>Show destructive dialog</VLKButton>
                <VLKAlertDialog
                  open={openAlert}
                  onOpenChange={setOpenAlert}
                  title="Emergency lock 123456782"
                  tone="destructive"
                  actions={<>
                    <VLKButton variant="ghost" onClick={() => setOpenAlert(false)}>Cancel</VLKButton>
                    <VLKButton variant="destructive" onClick={() => { setOpenAlert(false); toast.success("Lock triggered (demo)"); }}>Confirm lock</VLKButton>
                  </>}
                >
                  Locking this identity halts all verification attempts and writes an immutable audit entry.
                </VLKAlertDialog>
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Empty & data</VLKCardTitle></VLKCardHeader>
              <VLKCardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <VLKEmptyState icon={Bell} title="Inbox zero" description="You have no notifications." />
                <VLKDataTable
                  columns={[
                    { key: "id", header: "ID", mono: true },
                    { key: "name", header: "Name" },
                    { key: "risk", header: "Risk", render: (r) => <VLKRiskMeter level={r.risk} /> },
                  ]}
                  rows={[
                    { id: "123456782", name: "Ada Ojo", risk: "low" },
                    { id: "234567899", name: "Musa Bello", risk: "high" },
                  ]}
                />
              </VLKCardContent>
            </VLKCard>

            <VLKCard>
              <VLKCardHeader><VLKCardTitle>Avatars · VLKUserAvatar</VLKCardTitle></VLKCardHeader>
              <VLKCardContent className="flex gap-4 items-center">
                <VLKUserAvatar name="Ada Ojo" size={32} />
                <VLKUserAvatar name="Musa Bello" size={48} />
                <VLKUserAvatar name="V L" size={64} />
              </VLKCardContent>
            </VLKCard>
          </>
        )}

        {tab === "patterns" && (
          <VLKCard>
            <VLKCardHeader><VLKCardTitle>Design principles</VLKCardTitle></VLKCardHeader>
            <VLKCardContent className="prose prose-sm max-w-none dark:prose-invert">
              <ul className="text-sm space-y-2">
                <li>Sharp corners (2–4px). No neumorphism.</li>
                <li>Border-first separation. Shadows only for overlays.</li>
                <li>Left-aligned. Never center dense data.</li>
                <li>Monospace for identifiers, timestamps, hashes.</li>
                <li>Every interactive element has <code>data-testid</code> and visible focus ring.</li>
                <li>Motion is fast (150–200ms) and deliberate.</li>
                <li>Dark mode is the default.</li>
              </ul>
            </VLKCardContent>
          </VLKCard>
        )}
      </div>
    </div>
  );
}
